<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { z } from 'zod';
import { UiAvatar, UiButton, UiForm, UiFormField, UiInputField, UiText, generateInitials, useApi, useSharedLink } from '@shopexperts/shared';
import { useAuthCheck } from '@/app/composables/leads/useAuthCheck';
import LoginForm, { type LoginAuthResult } from '@/app/components/leads/LoginForm.vue';

interface ConsultationExpertSummary {
  id?: string | number;
  slug: string;
  name: string;
  displayRole: string;
  avatar?: string;
  bookingCallUrl?: string;
}

interface ConsultationLinkData {
  expert: ConsultationExpertSummary | null;
}

interface ConsultationFormValues {
  fullName: string;
  email: string;
  companyName: string;
  companyWebsite: string;
}

type Step = 'client-confirm' | 'form' | 'login';
type SubmissionErrorCode =
  | 'validation'
  | 'auth-required'
  | 'forbidden'
  | 'missing-booking-url'
  | 'missing-expert'
  | 'unknown';

const knownErrorMessages: Record<SubmissionErrorCode, string> = {
  validation: 'Please check the details and try again.',
  'auth-required': 'Please sign in to continue with this email.',
  forbidden: "This email can't be used for this request. Please use another email.",
  'missing-booking-url': "This expert isn't accepting booking requests right now.",
  'missing-expert': 'We could not identify the selected expert. Please close this form and try again.',
  unknown: 'Something went wrong while submitting your request. Please try again.',
};

function normalizeWebsiteUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const candidate = /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const url = new URL(candidate);
    if (!['http:', 'https:'].includes(url.protocol) || !url.hostname.includes('.')) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

const consultationFormSchema = z.object({
  fullName: z.preprocess(
    (value) => typeof value === 'string' ? value : '',
    z.string().trim().min(1, 'Please enter your full name.'),
  ),
  email: z.preprocess(
    (value) => typeof value === 'string' ? value : '',
    z.string().trim().min(1, 'Please enter your email address.').email('Please enter a valid email address.'),
  ),
  companyName: z.preprocess(
    (value) => typeof value === 'string' ? value : '',
    z.string().trim().min(1, 'Please enter your company name.'),
  ),
  companyWebsite: z.preprocess(
    (value) => typeof value === 'string' ? value : '',
    z.string()
      .trim()
      .min(1, 'Please enter your company website.')
      .refine((value) => Boolean(normalizeWebsiteUrl(value)), 'Please enter a valid company website URL.'),
  ),
});

const route = useRoute();
const api = useApi();
const loginFormRef = useTemplateRef<InstanceType<typeof LoginForm>>('loginFormRef');
const websiteOrigin = new URL(
  import.meta.env.VITE_WEBSITE_URL || import.meta.env.VITE_PUBLIC_SITE_URL || 'https://shopexperts.com',
  window.location.origin,
).origin;

const channelId = computed(() => {
  const value = route.query.channelId;
  return typeof value === 'string' && value ? value : 'free-consultation';
});

const fallbackExpert: ConsultationExpertSummary = {
  slug: '',
  name: 'Selected expert',
  displayRole: 'Expert',
};

const { data: linkData, connected, emitEvent } = useSharedLink<ConsultationLinkData>({
  name: 'shopexperts-free-consultation',
  channelId: channelId.value,
  mode: 'bidirectional',
  targetOrigin: websiteOrigin,
  default: {
    expert: null,
  },
});

const { user, client, isAuthenticated, isClient, refetch } = useAuthCheck();
const hasChosenAnotherEmail = ref(false);
const isSubmitting = ref(false);
const isRedirecting = ref(false);
const localError = ref('');
const pendingValues = ref<ConsultationFormValues | null>(null);

const form = ref<ConsultationFormValues>({
  fullName: '',
  email: '',
  companyName: '',
  companyWebsite: '',
});

const expert = computed(() => linkData.value.expert ?? fallbackExpert);
const expertInitials = computed(() => generateInitials(expert.value.name));
const expertId = computed(() => {
  const id = expert.value.id;
  if (typeof id === 'number') return id;
  if (typeof id === 'string' && id.trim()) {
    const parsed = Number(id);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
});
const isAuthenticatedClient = computed(() => isAuthenticated.value && isClient.value);
const step = computed<Step>(() => {
  if (pendingValues.value) return 'login';
  if (isAuthenticatedClient.value && !hasChosenAnotherEmail.value) return 'client-confirm';
  return 'form';
});
const clientDisplayName = computed(() => {
  const fullName = [user.value?.first_name, user.value?.last_name].filter(Boolean).join(' ').trim();
  return fullName || client.value?.company_name || user.value?.email || 'your account';
});
const clientEmail = computed(() => user.value?.email || '');
const clientCompany = computed(() => client.value?.company_name || '');
const clientRequestText = computed(() =>
  `We'll send this booking request using ${clientEmail.value}${clientCompany.value ? ` from ${clientCompany.value}` : ''}.`
);
const isBusy = computed(() => isSubmitting.value || isRedirecting.value);
const busyMessage = computed(() => isRedirecting.value ? 'Opening booking calendar...' : 'Submitting request...');
const isDirty = computed(() => Object.values(form.value).some((value) => typeof value === 'string' && value.trim().length > 0));

const payload = computed(() => ({
  values: { ...form.value },
  expert: linkData.value.expert,
  connected: connected.value,
  step: step.value,
}));

watch([user, client, isAuthenticatedClient], ([nextUser, nextClient, isClientAccount]) => {
  if (!isClientAccount) return;

  if (!form.value.email && nextUser?.email) {
    form.value.email = nextUser.email;
  }

  if (!form.value.fullName) {
    const fullName = [nextUser?.first_name, nextUser?.last_name].filter(Boolean).join(' ').trim();
    if (fullName) {
      form.value.fullName = fullName;
    }
  }

  if (!form.value.companyName && nextClient?.company_name) {
    form.value.companyName = nextClient.company_name;
  }
}, { immediate: true });

watch(isDirty, (dirty) => {
  emitEvent('dirty-change', { dirty });
});

watch(form, () => {
  localError.value = '';
  emitEvent('values-change', payload.value);
}, { deep: true });

watch(step, async () => {
  emitResize();
  if (step.value === 'login' && pendingValues.value?.email) {
    await nextTick();
    await loginFormRef.value?.setEmail(pendingValues.value.email);
    if (!isBusy.value) {
      await loginFormRef.value?.focusPassword();
    }
  }
});

watch(isBusy, () => {
  emitResize();
});

function emitResize() {
  requestAnimationFrame(() => {
    const height = Math.ceil(document.body.scrollHeight || document.documentElement.scrollHeight || 644);
    emitEvent('resize', { height });
  });
}

function emitSubmissionError(code: SubmissionErrorCode, message = knownErrorMessages[code], raw?: unknown) {
  emitEvent('submission-error', {
    code,
    message,
    status: extractStatus(raw),
  });
  if (code === 'unknown') {
    console.error('[EmbeddedFreeConsultation] unknown submission error', raw);
  }
}

function extractStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const candidate = error as { response?: { status?: number } };
  return candidate.response?.status;
}

function normalizeApiError(error: unknown): SubmissionErrorCode {
  const status = extractStatus(error);
  if (status === 401) return 'auth-required';
  if (status === 403) return 'forbidden';
  if (status === 422) return 'validation';
  return 'unknown';
}

function validateFormValues(values: ConsultationFormValues): boolean {
  if (!expertId.value) {
    localError.value = knownErrorMessages['missing-expert'];
    emitSubmissionError('missing-expert');
    return false;
  }
  if (!expert.value.bookingCallUrl) {
    localError.value = knownErrorMessages['missing-booking-url'];
    emitSubmissionError('missing-booking-url');
    return false;
  }
  const normalizedWebsite = normalizeWebsiteUrl(values.companyWebsite);
  if (!normalizedWebsite) {
    localError.value = 'Please enter a valid company website URL.';
    return false;
  }
  values.companyWebsite = normalizedWebsite;
  return true;
}

function toSubmitBody(values: ConsultationFormValues) {
  return {
    expert_id: expertId.value!,
    name: values.fullName.trim(),
    email: values.email.trim(),
    company_name: values.companyName.trim(),
    company_website: normalizeWebsiteUrl(values.companyWebsite) ?? values.companyWebsite.trim(),
  };
}

async function submitBookingCall(values?: ConsultationFormValues) {
  if (!expertId.value) {
    emitSubmissionError('missing-expert');
    return;
  }

  isSubmitting.value = true;
  try {
    const result = await api.public.bookingCallLeads.submit({
      body: values ? toSubmitBody(values) : { expert_id: expertId.value },
    });

    if (!result.booking_call_url) {
      emitSubmissionError('missing-booking-url');
      return;
    }

    isRedirecting.value = true;
    emitEvent('submitted', result);
    console.info('[EmbeddedFreeConsultation] submitted');
  } catch (error) {
    const code = normalizeApiError(error);
    emitSubmissionError(code, knownErrorMessages[code], error);
  } finally {
    if (!isRedirecting.value) {
      isSubmitting.value = false;
    }
  }
}

async function handleFormSubmit(submittedValues: ConsultationFormValues) {
  const values = { ...submittedValues };
  if (!validateFormValues(values)) return;

  localError.value = '';
  isSubmitting.value = true;

  try {
    const authRequirement = await api.public.bookingCallLeads.checkAuth({
      body: { email: values.email.trim() },
    });

    if (authRequirement.user_type === 'expert' || authRequirement.user_type === 'admin') {
      emitSubmissionError('forbidden');
      return;
    }

    if (authRequirement.requires_login) {
      pendingValues.value = values;
      return;
    }

    await submitBookingCall(values);
  } catch (error) {
    const code = normalizeApiError(error);
    emitSubmissionError(code, knownErrorMessages[code], error);
  } finally {
    isSubmitting.value = false;
  }
}

async function handleClientConfirmSubmit() {
  if (isBusy.value) return;
  await submitBookingCall();
}

function handleUseAnotherEmail() {
  if (isBusy.value) return;
  hasChosenAnotherEmail.value = true;
  pendingValues.value = null;
}

async function handleAuthenticated(data: LoginAuthResult) {
  if (data.type !== 'client') {
    emitSubmissionError('forbidden');
    pendingValues.value = null;
    return;
  }

  await refetch();
  const values = pendingValues.value;
  if (values) {
    await submitBookingCall(values);
  }
  if (!isRedirecting.value) {
    pendingValues.value = null;
  }
}

function handleLoginError(message: string) {
  if (isRedirecting.value) return;
  emitSubmissionError('auth-required', message || 'Invalid email or password.');
  emitResize();
}

function handleCancel() {
  if (isBusy.value) return;
  emitEvent('cancel', payload.value);
}

onMounted(() => {
  emitEvent('ready', { channelId: channelId.value });
  emitResize();
});
</script>

<template>
  <main class="bg-white font-archivo text-se-dark">
    <section
      class="relative mx-auto flex w-full max-w-[500px] flex-col gap-6 overflow-hidden rounded-lg bg-white p-6"
      data-testid="embedded-free-consultation-form"
    >
      <div
        v-if="isBusy"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/85 px-6 text-center backdrop-blur-[1px]"
        data-testid="consultation-loading-state"
      >
        <div class="h-8 w-8 animate-spin rounded-full border-2 border-se-gray-300 border-t-se-dark" />
        <UiText
          as="p"
          :text="busyMessage"
          class="text-[15px] font-medium leading-6 text-se-dark"
        />
      </div>
      <header class="flex flex-col gap-2">
        <UiText
          as="h1"
          text="Book a free consultation"
          class="text-[24px] font-medium leading-8 text-se-dark"
        />
        <UiText
          as="p"
          text="Fill in a few details below, and we'll take you straight to expert's calendar so you can pick a time that works for you."
          class="text-[16px] font-normal leading-6 text-se-dark"
        />
      </header>

      <section class="flex w-full items-center gap-4 rounded-lg border border-se-gray-300 bg-white px-4 py-2">
        <UiAvatar
          :url="expert.avatar"
          :initials="expertInitials"
          :name="expert.name"
          size="xs"
          :responsive="false"
          class="size-12"
        />
        <div class="flex min-w-0 flex-col">
          <UiText
            as="span"
            :text="expert.name"
            class="truncate text-[16px] font-semibold leading-6 text-se-dark"
          />
          <UiText
            as="span"
            :text="expert.displayRole"
            class="truncate text-[14px] font-normal leading-[22px] text-se-dark"
          />
        </div>
      </section>

      <section v-if="step === 'client-confirm'" class="flex flex-col gap-5" data-testid="authenticated-client-confirmation">
        <div class="rounded-lg border border-se-gray-300 bg-se-gray-100 p-4">
          <UiText
            as="p"
            :text="`Requesting as ${clientDisplayName}`"
            class="text-[16px] font-semibold leading-6 text-se-dark"
          />
          <UiText
            as="p"
            :text="clientRequestText"
            class="mt-1 text-[14px] leading-[22px] text-se-gray-700"
          />
        </div>

        <footer class="flex items-center justify-between gap-4">
          <UiButton
            type="button"
            label="Use another email"
            variant="outline"
            :show-arrow="false"
            :disabled="isBusy"
            @click="handleUseAnotherEmail"
          />
          <UiButton
            type="button"
            label="Submit request"
            variant="primary"
            :show-arrow="false"
            :disabled="isBusy"
            @click="handleClientConfirmSubmit"
          />
        </footer>
      </section>

      <UiForm
        v-else-if="step === 'form'"
        v-model="form"
        :schema="consultationFormSchema"
        @submit="handleFormSubmit"
      >
        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-6">
            <UiFormField name="fullName" label="Full Name" :label-capitalize="false" type="text" class="consultation-field">
              <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                <UiInputField
                  :id="id"
                  name="fullName"
                  :model-value="modelValue as string"
                  placeholder="Enter your full name"
                  :invalid="invalid"
                  :error="error"
                  :disabled="isBusy"
                  @update:model-value="onUpdateModelValue"
                  @validate="onValidate"
                />
              </template>
            </UiFormField>

            <UiFormField
              name="email"
              label="Your email"
              inline-hint="work email preferred"
              :label-capitalize="false"
              type="email"
              class="consultation-field"
            >
              <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                <UiInputField
                  :id="id"
                  name="email"
                  type="email"
                  :model-value="modelValue as string"
                  placeholder="yourname@company.com"
                  :invalid="invalid"
                  :error="error"
                  :disabled="isBusy"
                  @update:model-value="onUpdateModelValue"
                  @validate="onValidate"
                />
              </template>
            </UiFormField>

            <UiFormField name="companyName" label="Company name" :label-capitalize="false" type="text" class="consultation-field">
              <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                <UiInputField
                  :id="id"
                  name="companyName"
                  :model-value="modelValue as string"
                  placeholder="Enter company name"
                  :invalid="invalid"
                  :error="error"
                  :disabled="isBusy"
                  @update:model-value="onUpdateModelValue"
                  @validate="onValidate"
                />
              </template>
            </UiFormField>

            <UiFormField name="companyWebsite" label="Company website" :label-capitalize="false" type="text" class="consultation-field">
              <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                <UiInputField
                  :id="id"
                  name="companyWebsite"
                  :model-value="modelValue as string"
                  placeholder="www.company.com"
                  :invalid="invalid"
                  :error="error"
                  :disabled="isBusy"
                  @update:model-value="onUpdateModelValue"
                  @validate="onValidate"
                />
              </template>
            </UiFormField>
          </div>

          <UiText
            v-if="localError"
            as="p"
            variant="p-small"
            :text="localError"
            class="text-se-error"
            data-testid="consultation-local-error"
          />

          <footer class="flex items-center justify-between gap-4">
            <UiButton
              type="button"
              label="Cancel"
              variant="outline"
              :show-arrow="false"
              :disabled="isBusy"
              @click="handleCancel"
            />
            <UiButton
              type="submit"
              label="See Available Times"
              variant="primary"
              :show-arrow="false"
              :disabled="isBusy"
            />
          </footer>
        </div>
      </UiForm>

      <section v-else class="flex flex-col gap-6" data-testid="consultation-login-step">
        <UiText
          as="p"
          text="This email belongs to an existing client account. Sign in to submit your booking request."
          class="text-[15px] leading-6 text-se-dark"
        />
        <LoginForm
          ref="loginFormRef"
          :show-toast="false"
          :disabled="isBusy"
          @authenticated="handleAuthenticated"
          @error="handleLoginError"
        />
        <footer class="flex items-center justify-between gap-4">
          <UiButton
            type="button"
            label="Back"
            variant="outline"
            :show-arrow="false"
            :disabled="isBusy"
            @click="!isBusy && (pendingValues = null)"
          />
          <UiButton
            type="button"
            label="Sign in and submit"
            variant="primary"
            :show-arrow="false"
            :disabled="isBusy"
            @click="loginFormRef?.triggerSubmit()"
          />
        </footer>
      </section>
    </section>
  </main>
</template>

<style scoped>
.consultation-field :deep(.w-full.flex.flex-col.relative > .flex.flex-col.mt-1) {
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: 4px;
  min-height: 16px;
}
</style>
